/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-filename-extension */
import type { AppContext, AppProps } from 'next/app';
import { NextComponentType } from 'next';
import Head from 'next/head';
import Router from 'next/router';
import { useReportWebVitals } from 'next/web-vitals';
import NProgress from 'nprogress';
import React from 'react';
import { CssBaseline, ThemeProvider, Theme } from '@mui/material';
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import App from 'next/app';
import { createEmotionSsrAdvancedApproach } from 'tss-react/next/pagesDir';
import { ModalHost } from '@ui/Modal';
import { theme } from '../theme';
import * as gtag from '../services/gtag';
import EventTrackingService from '../services/EventTrackingService';
import { init as initSentry } from '../util/sentry';
import { hideZendeskWebWidget } from '../services/zendesk';
import withApollo from '../lib/withApollo';
import ReduxProvider from '../store/Provider';
import { PromptsProvider } from '../context/prompts/PromptsContext';
import AppCookieProvider from '../lib/AppCookieProvider';
import { AppProvider } from '../context/AppContext';
import ErrorBoundary from '../components/ErrorBoundary';
import Toaster from '../components/Toaster';
import '@ui/Modal/manifest';
import '@ui/theme/index.css';

const { augmentDocumentWithEmotionCache, withAppEmotionCache } =
  createEmotionSsrAdvancedApproach({ key: 'css' });

export { augmentDocumentWithEmotionCache };

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

initSentry();

let loadingStatusPerRoute: string | null = null;
Router.events.on('routeChangeStart', url => {
  loadingStatusPerRoute = url;
  setTimeout(() => {
    if (loadingStatusPerRoute === url) {
      NProgress.start();
    }
  }, 200);
});
Router.events.on('routeChangeComplete', url => {
  if (loadingStatusPerRoute === url) {
    if (NProgress.status && NProgress.status > 0) NProgress.done();
    loadingStatusPerRoute = null;
  }
});
Router.events.on('routeChangeError', (err, url) => {
  if (loadingStatusPerRoute === url) {
    if (NProgress.status && NProgress.status > 0) NProgress.done();
    loadingStatusPerRoute = null;
  }
});

Router.events.on('routeChangeComplete', url => {
  gtag.pageview(url);
});

interface NewAppProps extends AppProps {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  Component: NextComponentType & {
    Layout?: React.ComponentType<{ children: React.ReactNode }>;
  };
}

const NewApp = ({
  router,
  Component,
  pageProps,
  apolloClient,
}: NewAppProps) => {
  useReportWebVitals(metric => {
    switch (metric.name) {
      case 'LCP':
      case 'INP':
      case 'CLS':
      case 'TTFB':
      case 'FCP':
      case 'FID':
        EventTrackingService.track({
          type: metric.name,
          data: {
            value: metric.value,
            rating: metric.rating,
          },
        });
        break;
      default:
        break;
    }
  });

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <ErrorBoundary>
        <ApolloProvider client={apolloClient}>
          <ReduxProvider>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <AppProvider>
                <AppCookieProvider>
                  <PromptsProvider>
                    {Component.Layout ? (
                      <Component.Layout>
                        <Component {...pageProps} />
                      </Component.Layout>
                    ) : (
                      <Component {...pageProps} />
                    )}
                  </PromptsProvider>
                </AppCookieProvider>
              </AppProvider>
              <Toaster />
              <ModalHost />
            </ThemeProvider>
          </ReduxProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  );
};

NewApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  return { ...appProps };
};

NewApp.componentDidMount = () => {
  // Hide the Zendesk widget until the user has authenticated.
  hideZendeskWebWidget();
};

NewApp.displayName = 'NewApp';

export default withApollo(withAppEmotionCache(NewApp));
